import { FC, useEffect } from 'react';
import { Grid, Typography, Divider } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useUsers } from '../../contexts/users.context';
import { VisitorUser, VisitorStatus, UserStatus } from '../../users.interfaces';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { zDateStringFromInput, zOptionalDateStringFromInput, zOptionalNumberFromInput } from 'src/utils/zodFormHelpers';
import { useCreateVisitorUser } from '../../hooks/useVisitorHooks';

type VisitorFormData = {
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  email: string;
  phone: string;
  userName: string;
  userPassword: string;
  visitDate: string;
  visitTime: string;
  visitDuration?: number;
  visitPurpose: string;
  hostPerson: string;
  hostDepartment: string;
  visitStatus: VisitorStatus;
  idDocumentType: string;
  idDocumentNumber: string;
  securityClearance: 'basic' | 'standard' | 'high';
  status: UserStatus;
  notes?: string;
  address: {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
  };
};

const getVisitorFormSchema = (translateText: (key: string) => string) =>
  z.object({
    firstName: z
      .string()
      .min(2, translateText('common.fieldRequired'))
      .max(50, 'First name must not exceed 50 characters'),
    lastName: z
      .string()
      .min(2, translateText('common.fieldRequired'))
      .max(50, 'Last name must not exceed 50 characters'),
    email: z
      .string()
      .email('Please enter a valid email address')
      .min(1, translateText('common.fieldRequired')),
    phone: z
      .string()
      .min(10, 'Phone number must be at least 10 digits')
      .max(15, 'Phone number must not exceed 15 digits'),
    userName: z.string().min(3, 'Username must be at least 3 characters'),
    userPassword: z.string().min(6, 'Password must be at least 6 characters'),
    visitDate: zDateStringFromInput({
      requiredMessage: translateText('common.fieldRequired') || undefined,
      message: translateText('common.dateInvalid') || undefined,
    }),
    visitTime: z.string().min(1, translateText('common.fieldRequired')),
    visitDuration: zOptionalNumberFromInput(),
    visitPurpose: z.string().min(1, translateText('common.fieldRequired')),
    hostPerson: z.string().min(1, translateText('common.fieldRequired')),
    hostDepartment: z.string().min(1, translateText('common.fieldRequired')),
    visitStatus: z.nativeEnum(VisitorStatus),
    idDocumentType: z.string().min(1, translateText('common.fieldRequired')),
    idDocumentNumber: z.string().min(1, translateText('common.fieldRequired')),
    securityClearance: z.enum(['basic', 'standard', 'high']),
    status: z.nativeEnum(UserStatus),
    dateOfBirth: zOptionalDateStringFromInput({ message: 'Please enter a valid date (YYYY-MM-DD)' }),
    notes: z.string().optional(),
    address: z.object({
      street1: z.string().min(1, translateText('common.fieldRequired')),
      street2: z.string().optional(),
      city: z.string().min(1, translateText('common.fieldRequired')),
      state: z.string().min(1, translateText('common.fieldRequired')),
      pinCode: z.string().min(1, translateText('common.fieldRequired')),
      country: z.string().min(1, translateText('common.fieldRequired')),
    }),
  });

const getVisitorStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: VisitorStatus.SCHEDULED,
      value: translateText('users.options.visitorStatus.scheduled'),
    },
    {
      key: VisitorStatus.CHECKED_IN,
      value: translateText('users.options.visitorStatus.checkedIn'),
    },
    {
      key: VisitorStatus.CHECKED_OUT,
      value: translateText('users.options.visitorStatus.checkedOut'),
    },
    {
      key: VisitorStatus.CANCELLED,
      value: translateText('users.options.visitorStatus.cancelled'),
    },
    {
      key: VisitorStatus.COMPLETED,
      value: translateText('users.options.visitorStatus.completed'),
    },
  ];

const getStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: UserStatus.ACTIVE,
      value: translateText('users.options.status.active'),
    },
    {
      key: UserStatus.INACTIVE,
      value: translateText('users.options.status.inactive'),
    },
    {
      key: UserStatus.PENDING,
      value: translateText('users.options.status.pending'),
    },
    {
      key: UserStatus.BLOCKED,
      value: translateText('users.options.status.blocked'),
    },
  ];

const getSecurityClearanceOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: 'basic',
      value: translateText('users.forms.fields.securityClearance.basic'),
    },
    {
      key: 'standard',
      value: translateText('users.forms.fields.securityClearance.standard'),
    },
    {
      key: 'high',
      value: translateText('users.forms.fields.securityClearance.high'),
    },
  ];

const getDocumentTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: 'aadhar',
      value: translateText('users.forms.fields.idDocumentType.aadhar'),
    },
    {
      key: 'passport',
      value: translateText('users.forms.fields.idDocumentType.passport'),
    },
    {
      key: 'drivingLicense',
      value: translateText('users.forms.fields.idDocumentType.drivingLicense'),
    },
    { key: 'pan', value: translateText('users.forms.fields.idDocumentType.pan') },
    {
      key: 'other',
      value: translateText('users.forms.fields.idDocumentType.other'),
    },
  ];

const AddEditVisitorUserView: FC = () => {
  const { translateText } = useTranslation();
  const { state, closeAddEditVisitor } = useUsers();
  const {
    isAddEditVisitorOpen,
    isCreateMode,
    isEditMode,
    selectedUser,
    isLoading,
  } = state;

  const createMutation = useCreateVisitorUser();

  const visitorFormSchema = getVisitorFormSchema(translateText);
  const visitorStatusOptions = getVisitorStatusOptions(translateText);
  const statusOptions = getStatusOptions(translateText);
  const securityClearanceOptions = getSecurityClearanceOptions(translateText);
  const documentTypeOptions = getDocumentTypeOptions(translateText);

  const defaultValues: VisitorFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    userName: '',
    userPassword: '',
    visitDate: '',
    visitTime: '',
    visitDuration: undefined,
    visitPurpose: '',
    hostPerson: '',
    hostDepartment: '',
    dateOfBirth: '',
    visitStatus: VisitorStatus.SCHEDULED,
    idDocumentType: '',
    idDocumentNumber: '',
    securityClearance: 'basic',
    status: UserStatus.PENDING,
    notes: '',
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      pinCode: '',
      country: '',
    },
  };

  const methods = useForm<VisitorFormData>({
    resolver: zodResolver(visitorFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;

  useEffect(() => {
    if (isAddEditVisitorOpen && selectedUser && isEditMode) {
      const visitorUser = selectedUser as VisitorUser;
      reset({
        firstName: visitorUser.firstName || '',
        lastName: visitorUser.lastName || '',
        email: visitorUser.email || '',
        phone: visitorUser.phone || '',
        userName: '',
        userPassword: '',
        visitDate: visitorUser.visitDate || '',
        visitTime: visitorUser.visitTime || '',
        visitDuration: visitorUser.visitDuration,
        visitPurpose: visitorUser.visitPurpose || '',
        hostPerson: visitorUser.hostPerson || '',
        hostDepartment: visitorUser.hostDepartment || '',
        dateOfBirth: (visitorUser as any).dateOfBirth || '',
        visitStatus: visitorUser.visitStatus || VisitorStatus.SCHEDULED,
        idDocumentType: visitorUser.idDocumentType || '',
        idDocumentNumber: visitorUser.idDocumentNumber || '',
        securityClearance: visitorUser.securityClearance || 'basic',
        status: visitorUser.status || UserStatus.PENDING,
        notes: visitorUser.notes || '',
        address: { street1: '', city: '', state: '', pinCode: '', country: '' },
      });
    } else if (isAddEditVisitorOpen && isCreateMode) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddEditVisitorOpen, selectedUser, isEditMode, isCreateMode, reset]);

  const onSubmitForm = async (data: VisitorFormData) => {
    if (!isCreateMode) {
      // TODO: Implement update
      closeAddEditVisitor();
      return;
    }

    try {
      await createMutation.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        userName: data.userName,
        userPassword: data.userPassword,
        visitDate: data.visitDate,
        visitTime: data.visitTime,
        visitDuration: data.visitDuration,
        visitPurpose: data.visitPurpose,
        hostPerson: data.hostPerson,
        hostDepartment: data.hostDepartment,
        idDocumentType: data.idDocumentType,
        idDocumentNumber: data.idDocumentNumber,
        securityClearance: data.securityClearance as any,
        dateOfBirth: data.dateOfBirth,
        visitStatus: data.visitStatus,
        status: data.status,
        address: data.address,
        remarks: data.notes,
        userCategory: 'visitor',
      });
      closeAddEditVisitor();
    } catch (error) {
      console.error('Error creating visitor user:', error);
    }
  };

  const title = isEditMode
    ? translateText('users.forms.editVisitor') || 'Edit Visitor User'
    : translateText('users.forms.addVisitor') || 'Add Visitor User';

  return (
    <StructuredModal
      isOpen={isAddEditVisitorOpen}
      onClose={closeAddEditVisitor}
      title={title}
      drawerWidth="60vw"
      footerPositiveAction={handleSubmit(onSubmitForm)}
      footerPositiveActionText={
        isEditMode
          ? translateText('common.update')
          : translateText('common.save')
      }
      footerNegativeAction={closeAddEditVisitor}
      footerNegativeActionText={translateText('common.cancel')}
      isLoading={isLoading || createMutation.isPending}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmitForm)}
          style={{ width: '100%' }}
          data-testid="visitor-user-form"
        >
          <Grid
            container
            spacing={{ xs: 2, sm: 3 }}
            sx={{ p: { xs: 1, sm: 2 } }}
            data-testid="visitor-form-container"
          >
            <Grid item xs={12} data-testid="basic-details-section">
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
              >
                {translateText('users.forms.basicInformation') ||
                  'Basic Information'}
              </Typography>
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="first-name-field">
              <ControlledTextField
                control={control}
                name="firstName"
                id="first-name"
                label={
                  translateText('users.forms.fields.firstName') || 'First Name'
                }
                errorMessage={errors.firstName?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="last-name-field">
              <ControlledTextField
                control={control}
                name="lastName"
                id="last-name"
                label={
                  translateText('users.forms.fields.lastName') || 'Last Name'
                }
                errorMessage={errors.lastName?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="email-field">
              <ControlledTextField
                control={control}
                name="email"
                id="email"
                label={translateText('auth.email') || 'Email'}
                errorMessage={errors.email?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="phone-field">
              <ControlledTextField
                control={control}
                name="phone"
                id="phone"
                label={translateText('users.forms.fields.phone') || 'Phone'}
                errorMessage={errors.phone?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="dob-field">
              <ControlledTextField
                control={control}
                name="dateOfBirth"
                id="date-of-birth"
                label="Date of Birth"
                type="date"
                errorMessage={(errors as any).dateOfBirth?.message}
                isGridItem={false}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} data-testid="credentials-section">
              <Typography
                variant="h6"
                gutterBottom
                sx={{ mt: { xs: 1.5, sm: 2 }, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
              >
                {translateText('users.forms.loginCredentials') || 'Login Credentials'}
              </Typography>
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="username-field">
              <ControlledTextField
                control={control}
                name="userName"
                id="user-name"
                label={translateText('users.forms.fields.userName') || 'Username'}
                errorMessage={errors.userName?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="password-field">
              <ControlledTextField
                control={control}
                name="userPassword"
                id="user-password"
                label={translateText('users.forms.fields.userPassword') || 'Password'}
                type="password"
                errorMessage={errors.userPassword?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} data-testid="visit-details-section">
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  mt: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
                }}
              >
                {translateText('users.forms.visitDetails') || 'Visit Details'}
              </Typography>
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="visit-date-field">
              <ControlledTextField
                control={control}
                name="visitDate"
                id="visit-date"
                label={
                  translateText('users.forms.fields.visitDate') || 'Visit Date'
                }
                type="date"
                errorMessage={errors.visitDate?.message}
                required
                isGridItem={false}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="visit-time-field">
              <ControlledTextField
                control={control}
                name="visitTime"
                id="visit-time"
                label={
                  translateText('users.forms.fields.visitTime') || 'Visit Time'
                }
                type="time"
                errorMessage={errors.visitTime?.message}
                required
                isGridItem={false}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="visit-duration-field">
              <ControlledTextField
                control={control}
                name="visitDuration"
                id="visit-duration"
                label={
                  translateText('users.forms.fields.visitDuration') ||
                  'Duration (minutes)'
                }
                type="number"
                errorMessage={errors.visitDuration?.message}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="visit-status-field">
              <ControlledSelectField
                control={control}
                name="visitStatus"
                id="visit-status"
                label={
                  translateText('users.filters.visitStatus') || 'Visit Status'
                }
                itemList={visitorStatusOptions}
                errorMessage={errors.visitStatus?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} data-testid="visit-purpose-field">
              <ControlledTextField
                control={control}
                name="visitPurpose"
                id="visit-purpose"
                label={
                  translateText('users.forms.fields.visitPurpose') ||
                  'Visit Purpose'
                }
                errorMessage={errors.visitPurpose?.message}
                required
                multiline
                rows={2}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="host-person-field">
              <ControlledTextField
                control={control}
                name="hostPerson"
                id="host-person"
                label={
                  translateText('users.forms.fields.hostPerson') ||
                  'Host Person'
                }
                errorMessage={errors.hostPerson?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="host-department-field">
              <ControlledTextField
                control={control}
                name="hostDepartment"
                id="host-department"
                label={
                  translateText('users.forms.fields.hostDepartment') ||
                  'Host Department'
                }
                errorMessage={errors.hostDepartment?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} data-testid="security-section">
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  mt: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
                }}
              >
                {translateText('users.forms.securityInformation') ||
                  'Security Information'}
              </Typography>
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="id-document-type-field">
              <ControlledSelectField
                control={control}
                name="idDocumentType"
                id="id-document-type"
                label={
                  translateText('users.forms.fields.idDocumentType.label') ||
                  'ID Document Type'
                }
                itemList={documentTypeOptions}
                errorMessage={errors.idDocumentType?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="id-document-number-field">
              <ControlledTextField
                control={control}
                name="idDocumentNumber"
                id="id-document-number"
                label={
                  translateText('users.forms.fields.idDocumentNumber') ||
                  'ID Document Number'
                }
                errorMessage={errors.idDocumentNumber?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="security-clearance-field">
              <ControlledSelectField
                control={control}
                name="securityClearance"
                id="security-clearance"
                label={
                  translateText('users.forms.fields.securityClearance.label') ||
                  'Security Clearance'
                }
                itemList={securityClearanceOptions}
                errorMessage={errors.securityClearance?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="status-field">
              <ControlledSelectField
                control={control}
                name="status"
                id="status"
                label={translateText('common.status') || 'Status'}
                itemList={statusOptions}
                errorMessage={errors.status?.message}
                isRequired
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} data-testid="notes-field">
              <ControlledTextField
                control={control}
                name="notes"
                id="notes"
                label={translateText('common.remarks') || 'Notes'}
                errorMessage={errors.notes?.message}
                multiline
                rows={3}
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} data-testid="address-section">
              <Typography
                variant="h6"
                gutterBottom
                sx={{ mt: { xs: 1.5, sm: 2 }, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
              >
                {translateText('users.forms.addressDetails') || 'Address'}
              </Typography>
              <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="address-street1-field">
              <ControlledTextField
                control={control}
                name="address.street1"
                id="address-street1"
                label={translateText('users.forms.fields.street') || 'Street'}
                errorMessage={errors.address?.street1?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-testid="address-city-field">
              <ControlledTextField
                control={control}
                name="address.city"
                id="address-city"
                label={translateText('users.forms.fields.city') || 'City'}
                errorMessage={errors.address?.city?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={4} data-testid="address-state-field">
              <ControlledTextField
                control={control}
                name="address.state"
                id="address-state"
                label={translateText('users.forms.fields.state') || 'State'}
                errorMessage={errors.address?.state?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={4} data-testid="address-pincode-field">
              <ControlledTextField
                control={control}
                name="address.pinCode"
                id="address-pincode"
                label={translateText('users.forms.fields.zipCode') || 'Pin Code'}
                errorMessage={errors.address?.pinCode?.message}
                required
                isGridItem={false}
              />
            </Grid>

            <Grid item xs={12} sm={4} data-testid="address-country-field">
              <ControlledTextField
                control={control}
                name="address.country"
                id="address-country"
                label={translateText('users.forms.fields.country') || 'Country'}
                errorMessage={errors.address?.country?.message}
                required
                isGridItem={false}
              />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </StructuredModal>
  );
};

export default AddEditVisitorUserView;
